import { simplificarTitulo } from '../imports/cabecalho';
import type { JobRoleId } from '../shared/ids';

/**
 * A função cadastrada que um item de mão de obra da planilha quer dizer (Fase 29, RN-252).
 *
 * A planilha escreve o ofício como o canteiro fala — "CARPINTEIRO", "AJUDANTE", "PEDREIRO" — e a
 * obra cadastra a função como a folha a registra — "Oficial de Carpintaria", "Auxiliar de Obra",
 * "Oficial de Alvenaria". Sem a ligação, o item fica pela descrição e não pega o R$/h da folha
 * (D11); com ela, a hora custa o que a função custa.
 *
 * Só entre as funções **permitidas** no serviço (as da disciplina dele, `job_role_disciplines`):
 * ligar a uma função de outra disciplina faria a gravação recusar o envelope inteiro.
 *
 * Três jeitos, do mais seguro para o menos, e nunca um palpite entre dois candidatos:
 *
 * 1. o **nome igual** — sem acento nem caixa —, ou o código da função;
 * 2. o **ofício** do canteiro para o nome da folha (`OFICIOS`), pela palavra que começa a
 *    descrição: "SUPERVISOR DE ARMAÇÃO" é um supervisor;
 * 3. nada — o item fica pela descrição, e a tela pede a função.
 */

export interface FuncaoCandidata {
  readonly id: JobRoleId;
  readonly name: string;
  readonly code?: string;
}

/**
 * Os ofícios do canteiro e os nomes com que a folha da construtora os cadastra, na ordem de
 * preferência. O vocabulário da folha sai das planilhas do cliente (`DEFAULT_JOB_ROLES`).
 */
const OFICIOS: readonly (readonly [readonly string[], readonly string[]])[] = [
  [
    ['pedreiro', 'pedreira'],
    ['oficial de alvenaria', 'oficial de construcao'],
  ],
  [['carpinteiro', 'carpinteira'], ['oficial de carpintaria']],
  [['armador', 'armadora', 'ferreiro'], ['oficial de armacao']],
  [['ajudante', 'servente', 'auxiliar'], ['auxiliar de obra']],
  [['meio oficial'], ['meio oficial']],
  [['encanador', 'bombeiro hidraulico'], ['encanador']],
  [['eletricista'], ['eletricista']],
  [['pintor', 'pintora'], ['pintor']],
  [['supervisor', 'encarregado', 'mestre de obras', 'mestre de obra'], ['supervisor']],
  [['betoneiro', 'operador de betoneira'], ['operador de betoneira']],
  [['guincheiro', 'operador de guincho'], ['operador de guincho']],
];

/** A função de uma descrição de mão de obra, entre as permitidas — ou nenhuma. */
export function matchJobRole(
  descricao: string,
  funcoes: readonly FuncaoCandidata[],
): FuncaoCandidata | undefined {
  const texto = simplificarTitulo(descricao);
  if (texto === '' || funcoes.length === 0) return undefined;

  const iguais = funcoes.filter(
    (funcao) =>
      simplificarTitulo(funcao.name) === texto ||
      (funcao.code !== undefined && simplificarTitulo(funcao.code) === texto),
  );
  if (iguais.length === 1) return iguais[0];
  if (iguais.length > 1) return undefined;

  for (const [oficios, nomes] of OFICIOS) {
    const ehOficio = oficios.some((oficio) => texto === oficio || texto.startsWith(`${oficio} `));
    if (!ehOficio) continue;
    for (const nome of nomes) {
      const achadas = funcoes.filter((funcao) => simplificarTitulo(funcao.name) === nome);
      if (achadas.length === 1) return achadas[0];
    }
    return undefined;
  }
  return undefined;
}
